import { formatToHumanReadable } from "@/lib/format-data";

import { InventoryAction } from "@/types/enums/inventory-action";
import { PackageStatus } from "@/types/enums/package-status";
import type { PackageUsageFilters } from "@/types/inventory/package";
import type { PurchaseOrderFilters } from "@/types/inventory/purchase-order";
import { PurchaseOrderStatus } from "@/types/inventory/purchase-order-status";
import type { StockFilters } from "@/types/inventory/stock";
import type { SupplierFilters } from "@/types/inventory/supplier";

import {
  BOOLEAN_FILTER_OPTIONS,
  type FilterOption,
  LOCATION_SCOPE_DEFAULT,
  booleanFilter,
  dateRangeFilter,
  defineFilters,
  entitySetFilter,
  enumFilter,
  enumSetFilter,
  normalizeSet,
  setFilter,
  textFilter,
  tupleDateRangeFilter,
  tupleNumberRangeFilter,
} from "@/features/table-filters/contract";

const STOCK_LEVEL_STATUS_OPTIONS = [
  { label: "Low", value: "low" },
  { label: "Critical", value: "critical" },
  { label: "Out of Stock", value: "out-of-stock" },
] as const;

const PURCHASE_ORDER_STATUS_OPTIONS = Object.values(PurchaseOrderStatus).map(
  (status) => ({
    value: status,
    label: formatToHumanReadable(status),
  }),
);

const PACKAGE_USAGE_STATUS_OPTIONS = Object.values(PackageStatus).map(
  (status) => ({
    value: status,
    label: formatToHumanReadable(status),
  }),
);

function normalizeEntitySetWithoutCommas(value: unknown): string[] | undefined {
  const normalized = normalizeSet(value);
  return normalized?.some((item) => item.includes(","))
    ? undefined
    : normalized;
}

export const packageUsageFilterDefinitions =
  defineFilters<PackageUsageFilters>()([
    textFilter<PackageUsageFilters>({
      key: "patientName",
      label: "Patient Name/Email",
      operator: "contains",
      queryKey: "patient_name",
    }),
    tupleDateRangeFilter<PackageUsageFilters>({
      key: "purchasedDate",
      label: "Purchased Date",
      queryKey: "purchased_date",
    }),
    tupleDateRangeFilter<PackageUsageFilters>({
      key: "lastActivityDate",
      label: "Last Activity Date",
      queryKey: "last_activity_date",
    }),
    enumSetFilter<PackageUsageFilters>({
      key: "status",
      label: "Status",
      options: PACKAGE_USAGE_STATUS_OPTIONS,
      queryKey: "status",
    }),
    {
      ...entitySetFilter<PackageUsageFilters>({
        key: "locationIds",
        label: "Location",
        control: "location",
        queryKey: "location_ids",
        defaultPolicy: LOCATION_SCOPE_DEFAULT,
      }),
      normalizeRequest(request) {
        const next = { ...request };
        const value = normalizeEntitySetWithoutCommas(next.locationIds);
        if (value) next.locationIds = value;
        else delete next.locationIds;
        return next;
      },
      parseUrl(params) {
        const value = normalizeEntitySetWithoutCommas(
          params.getAll("locationIds"),
        );
        return value ? { locationIds: value } : {};
      },
    },
    textFilter<PackageUsageFilters>({
      key: "timezone",
      label: "Timezone",
      presented: false,
      urlPolicy: "none",
      queryKey: "timezone",
    }),
    textFilter<PackageUsageFilters>({
      key: "sortBy",
      label: "Sort by",
      presented: false,
      queryKey: "sort_by",
    }),
    enumFilter<PackageUsageFilters>({
      key: "sortDirection",
      label: "Sort direction",
      presented: false,
      queryKey: "sort_direction",
      options: [
        { value: "asc", label: "Ascending" },
        { value: "desc", label: "Descending" },
      ],
    }),
  ]);

export function createPurchaseOrderFilterDefinitions({
  suppliers,
}: {
  suppliers: { id: string; name: string }[];
}) {
  return defineFilters<PurchaseOrderFilters>()([
    enumSetFilter<PurchaseOrderFilters>({
      key: "status",
      label: "Status",
      options: PURCHASE_ORDER_STATUS_OPTIONS,
      queryKey: "status",
    }),
    entitySetFilter<PurchaseOrderFilters>({
      key: "supplierId",
      label: "Supplier",
      options: suppliers.map((supplier) => ({
        value: supplier.id,
        label: supplier.name,
      })),
      queryKey: "supplier_id",
    }),
    entitySetFilter<PurchaseOrderFilters>({
      key: "locationIds",
      label: "Location",
      control: "location",
      queryKey: "location_ids",
      defaultPolicy: LOCATION_SCOPE_DEFAULT,
    }),
    booleanFilter<PurchaseOrderFilters>({
      key: "isArchived",
      label: "Archived",
      presented: false,
      queryKey: "is_archived",
    }),
    entitySetFilter<PurchaseOrderFilters>({
      key: "ids",
      label: "Purchase order",
      presented: false,
      queryKey: "ids",
    }),
    textFilter<PurchaseOrderFilters>({
      key: "stockId",
      label: "Stock",
      presented: false,
      queryKey: "stock_id",
    }),
  ]);
}

export function createStockFilterDefinitions({
  categories,
  locations,
  suppliers = [],
  hasCoupa = false,
  coupaEnabled = false,
}: {
  categories: ({ name: string } | null)[];
  locations: { id: string; name: string }[];
  suppliers?: { id: string; name: string }[];
  /** COUPA is on at a clinic. */
  hasCoupa?: boolean;
  /** COUPA is on, at HQ too. */
  coupaEnabled?: boolean;
}) {
  const categoryOptions: FilterOption[] = [
    { label: "No category", value: "none" },
    ...categories
      .filter((category): category is { name: string } =>
        Boolean(category?.name),
      )
      .map((category) => ({ label: category.name, value: category.name })),
  ];

  return defineFilters<StockFilters>()([
    textFilter<StockFilters>({
      key: "name",
      label: "Name",
      operator: "contains",
    }),
    entitySetFilter<StockFilters>({
      key: "locationIds",
      label: "Locations",
      control: "location",
      options: locations.map((location) => ({
        label: location.name,
        value: location.id,
      })),
      urlPolicy: "none",
      queryKey: "location_ids",
      // Stock loads across every location by default; the user opts in.
      defaultPolicy: "none",
    }),
    setFilter<StockFilters>({
      key: "category",
      label: "Category",
      options: categoryOptions,
      queryKey: "category",
    }),
    enumFilter<StockFilters>({
      key: "supplierId",
      label: "Supplier",
      control: "select",
      options: [
        { label: "No supplier", value: "none" },
        ...suppliers.map((supplier) => ({
          label: supplier.name,
          value: supplier.id,
        })),
      ],
      queryKey: "supplier_id",
    }),
    tupleNumberRangeFilter<StockFilters>({
      key: "remainingQuantity",
      label: "Stock Qty",
      queryKey: "remainingQuantity",
    }),
    tupleNumberRangeFilter<StockFilters>({
      key: "expiringWithinDays",
      label: "Expiring Within (Days)",
      queryKey: "expiringWithinDays",
    }),
    enumSetFilter<StockFilters>({
      key: "stockLevelStatus",
      label: "Stock Level",
      options: STOCK_LEVEL_STATUS_OPTIONS,
      queryKey: "stockLevelStatus",
    }),
    ...(hasCoupa
      ? [
          enumFilter<StockFilters>({
            key: "coupaLinked",
            label: "Linked to Coupa",
            control: "boolean",
            options: BOOLEAN_FILTER_OPTIONS,
            queryKey: "coupa_linked",
          }),
        ]
      : []),
    ...(coupaEnabled
      ? [
          enumFilter<StockFilters>({
            key: "coupaExpensed",
            label: "Expensed in Coupa",
            control: "boolean",
            options: BOOLEAN_FILTER_OPTIONS,
            queryKey: "coupa_expensed",
          }),
        ]
      : []),
    entitySetFilter<StockFilters>({
      key: "ids",
      label: "Stock",
      presented: false,
      urlKey: "id",
      queryKey: "ids",
    }),
    textFilter<StockFilters>({
      key: "itemId",
      label: "Item",
      presented: false,
      queryKey: "item_id",
    }),
    booleanFilter<StockFilters>({
      key: "isArchived",
      label: "Archived",
      presented: false,
      queryKey: "is_archived",
    }),
  ]);
}

export const supplierFilterDefinitions = defineFilters<SupplierFilters>()([
  textFilter<SupplierFilters>({
    key: "name",
    label: "Name",
    operator: "contains",
  }),
  textFilter<SupplierFilters>({
    key: "city",
    label: "City",
    operator: "contains",
  }),
  textFilter<SupplierFilters>({
    key: "state",
    label: "State",
    operator: "contains",
    presented: false,
  }),
  booleanFilter<SupplierFilters>({
    key: "isArchived",
    label: "Archived",
    presented: false,
    queryKey: "is_archived",
  }),
]);

export interface StockActivityFilters {
  /** `yyyy-MM-dd`, widened to an absolute ISO day boundary at request time. */
  startDate?: string;
  endDate?: string;
  locationIds?: string[];
  actionTypes?: InventoryAction[];
}

const STOCK_ACTIVITY_ACTION_OPTIONS = [
  { value: InventoryAction.DRAWDOWN, label: "Drawdown" },
  { value: InventoryAction.RESTORED, label: "Restored" },
  { value: InventoryAction.SHIPMENT_RECEIVED, label: "Shipment Received" },
  { value: InventoryAction.SHIPMENT_UPDATED, label: "Shipment Updated" },
  {
    value: InventoryAction.SHIPMENT_ADJUSTED_UP,
    label: "Quantity Adjusted Up",
  },
  {
    value: InventoryAction.SHIPMENT_ADJUSTED_DOWN,
    label: "Quantity Adjusted Down",
  },
  { value: InventoryAction.SHIPMENT_ARCHIVED, label: "Shipment Archived" },
  { value: InventoryAction.STOCK_UPDATED, label: "Stock Updated" },
  { value: InventoryAction.SUPPLIER_TO_STOCK_LINKED, label: "Supplier Linked" },
  {
    value: InventoryAction.SUPPLIER_TO_STOCK_UPDATED,
    label: "Supplier Updated",
  },
  {
    value: InventoryAction.SUPPLIER_TO_STOCK_UNLINKED,
    label: "Supplier Unlinked",
  },
] as const;

/**
 * Stock activity timeline. Dates are plain `yyyy-MM-dd` here; the tab widens
 * them to absolute ISO day boundaries when it builds the request.
 */
export const stockActivityFilterDefinitions =
  defineFilters<StockActivityFilters>()([
    dateRangeFilter<StockActivityFilters>({
      startKey: "startDate",
      endKey: "endDate",
      label: "Date",
    }),
    setFilter<StockActivityFilters>({
      key: "locationIds",
      label: "Location",
      control: "location",
      defaultPolicy: LOCATION_SCOPE_DEFAULT,
    }),
    enumSetFilter<StockActivityFilters>({
      key: "actionTypes",
      label: "Event Type",
      options: STOCK_ACTIVITY_ACTION_OPTIONS,
    }),
  ]);
